import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Check {
  id: string;
  name: string;
  path: string;
  expectedStatus: number | null;
  timeoutMs: number;
  enabled: boolean;
}

/**
 * 006 US10 — deployment-checks configuration (rendered inside project
 * Settings). Checks run automatically against every fresh build; failing
 * results block promotion unless explicitly overridden.
 */
export function ChecksConfigSection({ orgSlug, projectSlug }: { orgSlug: string; projectSlug: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [path, setPath] = useState("/");
  const [expected, setExpected] = useState("");

  const q = useQuery({
    queryKey: ["checks-config", orgSlug, projectSlug],
    queryFn: () =>
      api.get<{ checks: Check[] }>(`/orgs/${orgSlug}/projects/${projectSlug}/checks`).then((r) => r.checks),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post(`/orgs/${orgSlug}/projects/${projectSlug}/checks`, {
        name,
        path,
        expectedStatus: expected ? Number(expected) : null,
      }),
    onSuccess: () => {
      setName("");
      setPath("/");
      setExpected("");
      qc.invalidateQueries({ queryKey: ["checks-config", orgSlug, projectSlug] });
    },
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      api.patch(`/orgs/${orgSlug}/projects/${projectSlug}/checks/${v.id}`, { enabled: v.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["checks-config", orgSlug, projectSlug] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${orgSlug}/projects/${projectSlug}/checks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["checks-config", orgSlug, projectSlug] }),
  });

  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" /> Deployment checks
        </h3>
        <span className="hidden sm:inline text-2xs text-subtle-foreground">
          run after every build · failing checks block promote (with audited override)
        </span>
      </div>

      {q.isLoading && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>}
      {!q.isLoading && (q.data?.length ?? 0) === 0 && (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          No checks configured. Add an HTTP check (e.g. <span className="font-mono">/</span> or{" "}
          <span className="font-mono">/api/health</span>) to catch builds that compile but serve a broken page.
        </div>
      )}

      {q.data?.map((c) => (
        <div key={c.id} className="of-row gap-3 items-center">
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium">{c.name}</span>
            <span className="font-mono text-xs text-muted-foreground ml-2 break-all">
              GET {c.path} → {c.expectedStatus ?? "≤ 399"}
            </span>
          </div>
          <label className="text-xs flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={c.enabled}
              onChange={(e) => patch.mutate({ id: c.id, enabled: e.target.checked })}
            />
            enabled
          </label>
          <Button
            variant="ghost"
            size="icon"
            title="Delete check"
            onClick={() => {
              if (window.confirm(`Delete check "${c.name}"?`)) del.mutate(c.id);
            }}
          >
            {del.isPending && del.variables === c.id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5 text-danger" />
            )}
          </Button>
        </div>
      ))}

      <div className="px-4 py-3 border-t border-border-muted space-y-2">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="homepage" className="h-8 w-36" />
          </div>
          <div className="space-y-1 flex-1 min-w-[160px]">
            <Label className="text-xs">Path</Label>
            <Input value={path} onChange={(e) => setPath(e.target.value)} className="h-8 font-mono text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Expect status (blank = any &lt;400)</Label>
            <Input
              value={expected}
              onChange={(e) => setExpected(e.target.value.replace(/\D/g, ""))}
              placeholder="200"
              className="h-8 w-32 font-mono"
            />
          </div>
          <Button variant="default" size="sm" disabled={!name || !path.startsWith("/") || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add check
          </Button>
        </div>
        {create.error instanceof ApiError && <div className="text-xs text-danger">{create.error.message}</div>}
      </div>
    </div>
  );
}
